import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'

import App from './App.tsx'
import { AuthProvider } from './contexts/AuthContext'
import { SocketProvider } from './contexts/SocketContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ConfirmProvider } from './contexts/ConfirmContext'
import ErrorBoundary from './components/ErrorBoundary'
import SplashScreen from './components/SplashScreen'
import './index.css'

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false
        }
        return failureCount < 3
      },
      // The API host sleeps when idle, so a request can fail purely because the
      // tab sat open for a while. Refetching on reconnect means the user comes
      // back to live data rather than a stale page or a stuck error.
      refetchOnReconnect: true,
    },
    mutations: {
      retry: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AuthProvider>
              <SocketProvider>
                <ConfirmProvider>
                  <SplashScreen />
                  <App />
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 4000,
                      // Toasts were hard-coded to a dark slate panel with near-white
                      // text, so in light mode they appeared as a black box that
                      // matched nothing else on screen. Resolving through the theme
                      // tokens keeps them in step with whichever theme is active.
                      style: {
                        background: 'rgb(var(--c-surface))',
                        color: 'rgb(var(--c-ink))',
                        border: '1px solid rgb(var(--c-line))',
                        borderRadius: '12px',
                        padding: '12px 16px',
                        fontSize: '14px',
                        maxWidth: '400px',
                        boxShadow: 'var(--shadow-depth)',
                      },
                      success: {
                        iconTheme: {
                          primary: '#10b981',
                          secondary: 'rgb(var(--c-surface))',
                        },
                      },
                      error: {
                        // Errors are the toasts users most need to finish reading.
                        duration: 6000,
                        iconTheme: {
                          primary: '#ef4444',
                          secondary: 'rgb(var(--c-surface))',
                        },
                      },
                    }}
                  />
                </ConfirmProvider>
              </SocketProvider>
            </AuthProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>,
)
